import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ContextMenu.tsx -- right-click menu for the desktop.
//
// Per design \u00a76.6 there are TWO menu variants:
//   - on a file/folder icon -> Preview / Open / Download / Rename / Move /
//     Archive / Delete / Properties
//   - on empty canvas       -> New folder / Upload / AI create / Refresh /
//     View options / Show trash
//
// The desktop owns the open/closed state and which variant to show; this
// component renders accordingly. Clicking outside closes; Esc closes.
import { useEffect, useRef } from 'react';
export function ContextMenu(props) {
    const ref = useRef(null);
    useEffect(() => {
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target))
                props.onClose();
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                props.onClose();
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [props]);
    const items = props.entry
        ? buildEntryItems(props)
        : buildCanvasItems(props);
    return (_jsx("div", { ref: ref, className: "context-menu", style: { left: props.x, top: props.y }, role: "menu", children: items.map((it, i) => (_jsxs("div", { children: [it.separatorBefore && _jsx("div", { className: "context-menu-sep", role: "separator" }), _jsxs("button", { type: "button", className: `context-menu-item ${it.destructive ? 'destructive' : ''}`, onClick: () => { props.onClose(); it.onClick(); }, disabled: it.disabled, role: "menuitem", children: [_jsx("span", { className: "label", children: it.label }), it.shortcut && _jsx("span", { className: "shortcut", children: it.shortcut })] })] }, i))) }));
}
function buildEntryItems(p) {
    const isFolder = p.entry?.type === 'catalog';
    const canModify = p.role === 'update' || p.role === 'full';
    const canDelete = p.role === 'full';
    return [
        { label: isFolder ? 'Open' : 'Preview', shortcut: isFolder ? 'Enter' : 'Space', onClick: p.onPreview ?? (() => { }) },
        ...(isFolder ? [] : [{ label: 'Open in browser', onClick: p.onOpen ?? (() => { }) }]),
        ...(isFolder ? [] : [{ label: 'Download', onClick: p.onDownload ?? (() => { }) }]),
        // Status (workbench I18): only on file entries (folders have no
        // digest pipeline). Re-opens the digest progress window so the
        // user can re-watch a previous upload's status, or check on an
        // in-flight digest after they closed the auto-opened window.
        ...(isFolder ? [] : [{ label: 'Status', onClick: p.onStatus ?? (() => { }) }]),
        { label: 'Rename', shortcut: 'F2', disabled: !canModify, onClick: p.onRename ?? (() => { }) },
        { label: 'Move to\u2026', disabled: !canModify, onClick: p.onMove ?? (() => { }) },
        ...(isFolder ? [] : [{ label: 'Archive', disabled: !canDelete, onClick: p.onArchive ?? (() => { }) }]),
        { label: 'Delete', shortcut: 'Delete', destructive: true, disabled: !canDelete, onClick: p.onDelete ?? (() => { }) },
        { label: 'Properties', separatorBefore: true, onClick: p.onProperties ?? (() => { }) },
    ];
}
function buildCanvasItems(p) {
    const canModify = p.role === 'update' || p.role === 'full';
    return [
        { label: 'New folder', disabled: !canModify, onClick: p.onNewFolder ?? (() => { }) },
        { label: 'Upload\u2026', onClick: p.onUpload ?? (() => { }) },
        { label: 'AI create\u2026', disabled: !canModify, onClick: p.onAICreate ?? (() => { }) },
        { label: 'Refresh', shortcut: 'F5', separatorBefore: true, onClick: p.onRefresh ?? (() => { }) },
        { label: 'Show trash', onClick: p.onShowTrash ?? (() => { }) },
    ];
}
